import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MapPin, TrendingUp, Clock, Zap, CheckCircle, RefreshCw } from 'lucide-react';

const ProjectDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shares, setShares] = useState(1);
  const [hasInterest, setHasInterest] = useState(false);
  const [interestStatus, setInterestStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchProject();
    if (user) {
      checkInterest();
    }
  }, [id, user, i18n.language]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}`, { params: { lang: i18n.language } });
      setProject(response.data);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error(t('projectDetail.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAvailability = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API}/projects/${id}`, { params: { lang: i18n.language } });
      setProject(response.data);
      toast.success(t('projectDetail.availabilityUpdated'));
    } catch (error) {
      console.error('Failed to refresh project:', error);
      toast.error(t('projectDetail.refreshFailed'));
    } finally {
      setRefreshing(false);
    }
  };

  const checkInterest = async () => {
    try {
      const response = await axios.get(`${API}/interest`);
      const interest = response.data.find(i => i.project_id === id);
      if (interest) {
        setHasInterest(true);
        setInterestStatus(interest.status);
      }
    } catch (error) {
      console.error('Failed to check interest:', error);
    }
  };

  const [interestForm, setInterestForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [showInterestForm, setShowInterestForm] = useState(false);

  // Pre-fill user data when showing the form
  useEffect(() => {
    if (showInterestForm && user) {
      setInterestForm({
        name: user.username || '',
        email: user.email || '',
        phone: user.phone || ''  // Pre-fill phone if available
      });
    }
  }, [showInterestForm, user]);

  const handleExpressInterest = async () => {
    if (!user) {
      navigate('/bess/login');
      return;
    }

    // Validate form
    if (!interestForm.name || !interestForm.email || !interestForm.phone) {
      toast.error(t('projectDetail.fillAllFields'));
      return;
    }

    // Confirmation dialog
    const confirmMessage = t('projectDetail.confirmInterest', { name: interestForm.name, email: interestForm.email, phone: interestForm.phone });
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.post(`${API}/interest`, {
        project_id: id,
        name: interestForm.name,
        email: interestForm.email,
        phone: interestForm.phone
      });
      toast.success(t('projectDetail.interestSuccess'));
      setHasInterest(true);
      setInterestStatus('pending');
      setShowInterestForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('projectDetail.interestError'));
    }
  };

  const handleInvest = async () => {
    if (!user) {
      navigate('/bess/login');
      return;
    }

    if (interestStatus !== 'approved') {
      toast.error(t('projectDetail.interestNotApproved'));
      return;
    }

    if (shares < 1 || shares > project.available_shares) {
      toast.error(t('projectDetail.shareRangeError', { max: project.available_shares }));
      return;
    }

    // Confirmation dialog
    const totalAmount = shares * project.price_per_share;
    const confirmMessage = t('projectDetail.confirmInvest', { title: project.title, shares, total: totalAmount.toLocaleString() });
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.post(`${API}/investments`, { project_id: id, shares });
      toast.success(t('projectDetail.investSuccess', { shares }));
      fetchProject();
      navigate('/bess/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('projectDetail.investFailed'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-lg text-slate-600">{t('projectDetail.loading')}</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('projectDetail.notFoundTitle')}</h2>
          <Button onClick={() => navigate('/bess/projects')}>{t('projectDetail.backToProjects')}</Button>
        </div>
      </div>
    );
  }

  const totalInvestment = shares * project.price_per_share;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12" data-testid="project-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-fadeIn">
          {/* Hero Image */}
          <div className="relative h-96 rounded-2xl overflow-hidden mb-8 shadow-xl">
            <img
              src={project.image_url}
              alt={project.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end">
              <div className="p-8 text-white">
                <span className={`badge ${project.status === 'active' ? 'badge-green' : 'badge-blue'} mb-3`}>
                  {project.status}
                </span>
                <h1 className="text-4xl md:text-5xl font-bold mb-2">{project.title}</h1>
                <div className="flex items-center text-lg">
                  <MapPin size={20} className="mr-2" />
                  {project.location}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div className="card">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('projectDetail.aboutTitle')}</h2>
                <p className="text-slate-700 leading-relaxed">{project.description}</p>
              </div>

              {/* Key Metrics */}
              <div className="card">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('projectDetail.overviewTitle')}</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                    <Zap className="mx-auto mb-2 text-green-600" size={32} />
                    <p className="text-sm text-slate-600 mb-1">{t('projectDetail.capacity')}</p>
                    <p className="text-2xl font-bold text-slate-900">{project.capacity_mw} MWh</p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                    <TrendingUp className="mx-auto mb-2 text-blue-600" size={32} />
                    <p className="text-sm text-slate-600 mb-1">{t('projectDetail.returnPa')}</p>
                    <p className="text-2xl font-bold text-green-600">{project.expected_return}%</p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg">
                    <Clock className="mx-auto mb-2 text-slate-600" size={32} />
                    <p className="text-sm text-slate-600 mb-1">{t('projectDetail.duration')}</p>
                    <p className="text-2xl font-bold text-slate-900">{project.duration_years} {t('projectDetail.years')}</p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-green-50 to-blue-100 rounded-lg">
                    <CheckCircle className="mx-auto mb-2 text-green-600" size={32} />
                    <p className="text-sm text-slate-600 mb-1">{t('projectDetail.available')}</p>
                    <p className="text-2xl font-bold text-slate-900">{project.available_shares}</p>
                  </div>
                </div>
              </div>

              {/* Payment Plan */}
              <div className="card">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('projectDetail.paymentPlanTitle')}</h2>
                <div className="space-y-4">
                  {Array.isArray(project.payment_stages) && project.payment_stages.map((percentage, index) => {
                    const description = project.payment_stage_descriptions?.[index] || '';
                    // Calculate amount for this stage based on price per share and selected shares
                    const amountPerShare = (project.price_per_share * percentage / 100);
                    const totalAmountForStage = (amountPerShare * shares);
                    return (
                      <div key={index} className="flex items-center p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900">
                            {description || t('projectDetail.stage', { n: index + 1 })}
                          </h4>
                          <p className="text-sm text-slate-600">{t('projectDetail.paymentOfTotal', { percentage })}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-900">{percentage}%</p>
                          <p className="text-sm text-slate-600">
                            {totalAmountForStage.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </p>
                          {shares > 1 && (
                            <p className="text-xs text-slate-500">
                              ({amountPerShare.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € {t('projectDetail.perShare')})
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Additional Images */}
              {project.images && project.images.length > 0 && (
                <div className="card">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('projectDetail.galleryTitle')}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {project.images.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`${project.title} - ${t('projectDetail.imageAlt', { n: index + 1 })}`}
                        className="w-full h-48 object-cover rounded-lg shadow-md hover:shadow-xl transition-all"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="card sticky top-20">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">{t('projectDetail.investTitle')}</h3>

                {/* Availability */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">{t('projectDetail.availableShares')}</span>
                    <span className="font-semibold">{project.available_shares}/{project.total_shares}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-green-500 to-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${(project.available_shares / project.total_shares) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Interest Status */}
                {hasInterest && (
                  <div className={`mb-6 p-4 rounded-lg border ${interestStatus === 'approved' ? 'bg-green-50 border-green-200' : interestStatus === 'pending' ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                    <p className="text-sm font-semibold">
                      {interestStatus === 'approved' && t('projectDetail.interestApproved')}
                      {interestStatus === 'pending' && t('projectDetail.interestPending')}
                      {interestStatus === 'rejected' && t('projectDetail.interestRejected')}
                    </p>
                  </div>
                )}

                {!hasInterest ? (
                  <div className="space-y-4">
                    {!showInterestForm ? (
                      <>
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                          <p className="text-sm text-slate-600 mb-2">
                            {project.open_for_investments
                              ? t('projectDetail.openForInvestmentNotice')
                              : t('projectDetail.closedNotice')}
                          </p>
                        </div>
                        <Button
                          onClick={() => {
                            if (!project.open_for_investments) {
                              toast.info(t('projectDetail.closedToast'));
                              return;
                            }
                            if (!user) {
                              // Redirect to registration if not logged in
                              toast.info(t('projectDetail.registerFirstToast'));
                              navigate('/bess/register');
                              return;
                            }
                            setShowInterestForm(true);
                          }}
                          disabled={!project.open_for_investments}
                          className={`w-full ${project.open_for_investments
                            ? 'bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                          data-testid="show-interest-form-btn"
                        >
                          {project.open_for_investments ? t('projectDetail.showInterest') : t('projectDetail.closedButton')}
                        </Button>
                      </>
                    ) : (
                      <div className="space-y-4">
                        <h3 className="font-semibold text-slate-900">{t('projectDetail.yourContactData')}</h3>
                        <div>
                          <Label htmlFor="interest_name">{t('projectDetail.name')}</Label>
                          <Input
                            id="interest_name"
                            value={interestForm.name}
                            onChange={(e) => setInterestForm({ ...interestForm, name: e.target.value })}
                            placeholder={t('projectDetail.namePlaceholder')}
                            data-testid="interest-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="interest_email">{t('projectDetail.email')}</Label>
                          <Input
                            id="interest_email"
                            type="email"
                            value={interestForm.email}
                            onChange={(e) => setInterestForm({ ...interestForm, email: e.target.value })}
                            placeholder="ihre@email.de"
                            data-testid="interest-email-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="interest_phone">{t('projectDetail.phone')}</Label>
                          <Input
                            id="interest_phone"
                            type="tel"
                            value={interestForm.phone}
                            onChange={(e) => setInterestForm({ ...interestForm, phone: e.target.value })}
                            placeholder="+49 123 456789"
                            data-testid="interest-phone-input"
                          />
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            onClick={handleExpressInterest}
                            className="flex-1 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white"
                            data-testid="submit-interest-btn"
                          >
                            {t('projectDetail.submit')}
                          </Button>
                          <Button
                            onClick={() => setShowInterestForm(false)}
                            variant="outline"
                            data-testid="cancel-interest-btn"
                          >
                            {t('projectDetail.cancel')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : interestStatus === 'rejected' ? (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-800 font-semibold mb-2">
                        {t('projectDetail.rejectedTitle')}
                      </p>
                      <p className="text-sm text-red-700">
                        {t('projectDetail.rejectedText')}
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        if (window.confirm(t('projectDetail.reapplyConfirm'))) {
                          setShowInterestForm(true);
                          setInterestStatus('pending'); // Reset locally for form display
                        }
                      }}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
                      data-testid="reapply-interest-btn"
                    >
                      {t('projectDetail.reapplyButton')}
                    </Button>
                  </div>
                ) : interestStatus === 'approved' ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="shares">{t('projectDetail.sharesLabel')}</Label>
                      <Input
                        id="shares"
                        type="number"
                        min="1"
                        max={project.available_shares}
                        value={shares}
                        onChange={(e) => setShares(parseInt(e.target.value) || 1)}
                        data-testid="shares-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {t('projectDetail.pricePerShare', { price: project.price_per_share.toLocaleString() })}
                      </p>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                      <div className="flex justify-between mb-2">
                        <span className="text-slate-600">{t('projectDetail.shares')}</span>
                        <span className="font-semibold">{shares}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="text-slate-600">{t('projectDetail.priceShare')}</span>
                        <span className="font-semibold">€{project.price_per_share.toLocaleString()}</span>
                      </div>
                      <div className="border-t border-slate-300 pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="font-bold text-slate-900">{t('projectDetail.total')}</span>
                          <span className="font-bold text-green-600 text-lg">€{totalInvestment.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={handleInvest}
                      className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white"
                      disabled={project.available_shares < 1}
                      data-testid="invest-btn"
                    >
                      {t('projectDetail.investNow')}
                    </Button>

                    <Button
                      onClick={handleRefreshAvailability}
                      variant="outline"
                      className="w-full mt-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                      disabled={refreshing}
                    >
                      {refreshing ? (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                          {t('projectDetail.refreshing')}
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          {t('projectDetail.checkAvailability')}
                        </>
                      )}
                    </Button>

                    {project.bank_details && (project.bank_details.iban || project.bank_details.account_holder) && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
                        <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
                          <span className="mr-2">🏦</span>
                          {t('projectDetail.bankDetailsTitle')}
                        </h4>
                        <div className="space-y-2 text-sm">
                          {project.bank_details.account_holder && (
                            <div>
                              <span className="text-slate-600">{t('projectDetail.recipient')}</span>
                              <span className="ml-2 font-medium">{project.bank_details.account_holder}</span>
                            </div>
                          )}
                          {project.bank_details.iban && (
                            <div>
                              <span className="text-slate-600">{t('projectDetail.iban')}</span>
                              <span className="ml-2 font-mono font-medium">{project.bank_details.iban}</span>
                            </div>
                          )}
                          {project.bank_details.bic && (
                            <div>
                              <span className="text-slate-600">{t('projectDetail.bic')}</span>
                              <span className="ml-2 font-mono font-medium">{project.bank_details.bic}</span>
                            </div>
                          )}
                          {project.bank_details.bank_name && (
                            <div>
                              <span className="text-slate-600">{t('projectDetail.bank')}</span>
                              <span className="ml-2 font-medium">{project.bank_details.bank_name}</span>
                            </div>
                          )}
                          {project.bank_details.purpose && (
                            <div className="pt-2 border-t border-blue-300">
                              <span className="text-slate-600">{t('projectDetail.purpose')}</span>
                              <div className="ml-2 font-medium text-blue-900 bg-blue-100 p-2 rounded mt-1">
                                {project.bank_details.purpose}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-slate-500 text-center">
                      {t('projectDetail.paymentNotice')}
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <p className="text-sm text-slate-700">
                      {t('projectDetail.pendingReview')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Investor-Only Detailed Information Section */}
          {user && interestStatus === 'approved' && (
            <div className="mt-8">
              <div className="card">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 mb-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    {t('projectDetail.detailedInfoTitle')}
                  </h2>
                  <p className="text-sm text-slate-600">
                    {t('projectDetail.detailedInfoSubtitle')}
                  </p>
                </div>

                <div className="space-y-8">
                  {/* 1. Grundstücksplan */}
                  {project.site_plan_image && (
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center">
                        <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">1</span>
                        {t('projectDetail.sitePlanTitle')}
                      </h3>
                      <div className="border rounded-lg overflow-hidden">
                        <img
                          src={project.site_plan_image}
                          alt={t('projectDetail.sitePlanTitle')}
                          className="w-full h-auto"
                        />
                      </div>
                    </div>
                  )}

                  {/* 2. Kennzahlen */}
                  {project.key_metrics && (
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center">
                        <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">2</span>
                        {t('projectDetail.keyMetricsTitle')}
                      </h3>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <p className="text-slate-700 whitespace-pre-line">{project.key_metrics}</p>
                      </div>
                    </div>
                  )}

                  {/* 3. Speichertechnologie */}
                  {project.storage_technology && (
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center">
                        <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">3</span>
                        {t('projectDetail.storageTechTitle')}
                      </h3>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <p className="text-slate-700 whitespace-pre-line">{project.storage_technology}</p>
                      </div>
                    </div>
                  )}

                  {/* 4. Besonderheiten */}
                  {project.special_features && (
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center">
                        <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">4</span>
                        {t('projectDetail.specialFeaturesTitle')}
                      </h3>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <p className="text-slate-700 whitespace-pre-line">{project.special_features}</p>
                      </div>
                    </div>
                  )}

                  {/* 5. Projektstatus */}
                  {project.project_status_details && (
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center">
                        <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">5</span>
                        {t('projectDetail.statusDetailsTitle')}
                      </h3>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <p className="text-slate-700 whitespace-pre-line">{project.project_status_details}</p>
                      </div>
                    </div>
                  )}

                  {/* Message if no detailed info available */}
                  {!project.site_plan_image && !project.key_metrics && !project.storage_technology && 
                   !project.special_features && !project.project_status_details && (
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 text-center">
                      <p className="text-slate-700">
                        {t('projectDetail.noDetailedInfo')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
